import { round } from './round';
import { getInvoiceDeduction } from './orderEffectiveAmount';

// When editing a booked (non-draft) invoice, the order's billed figures already
// include this invoice — so "already billed" and the open volume would be
// double-counted. Remove the edited invoice's own contribution so the numbers
// reflect what was billed by the *previous* invoices only. Draft invoices are
// not yet counted against the order, so no adjustment is needed.
export function excludeInvoiceFromOrder(order, invoice) {
  if (!order) return order;
  const isCounted = Boolean(invoice?.id) && !invoice.isDraft;
  if (!isCounted) return order;

  // Netto money this invoice bills per order position.
  const billedPerOrderPosition = {};
  (invoice.positions || []).forEach((pos) => {
    if (!pos.isOrderPosition || pos.orderPositionId == null) return;
    const money = round(Number(pos.amount) * Number(pos.price)) || 0;
    billedPerOrderPosition[pos.orderPositionId] =
      (billedPerOrderPosition[pos.orderPositionId] || 0) + money;
  });

  const positions = (order.positions || []).map((op) => {
    const ownBilled = billedPerOrderPosition[op.id];
    if (!ownBilled) return op;
    return { ...op, billedTotal: round((Number(op.billedTotal) || 0) - ownBilled) };
  });

  const invoices = (order.invoices || []).filter(
    (inv) => inv.id !== invoice.id,
  );

  return { ...order, positions, invoices };
}

// Maps a raw order (from GET /orders/:id) into the flattened order fields the
// order-based invoice stepper and its live preview rely on (orderPositions,
// previousInvoices, orderPrice, ...). Kept in one place so the initial edit
// state (NewProgressBilling) and the "Invoices" step (StepOrderInvoices) stay
// in sync — the preview reads these fields regardless of the active step.
export function buildOrderData(order, dataCollection = {}) {
  // The tax rate for the generated order lines comes from the order itself —
  // its positions carry the rate the order was written with (e.g. 0% for §13b
  // reverse charge). A single summary line can't represent a mixed-rate
  // order, so use the rate carrying the largest net volume. Only fall back
  // to the stored invoice value / template default when the order has no
  // rated position. `??` keeps a legitimate 0%.
  const rateVolumes = new Map();
  (order.positions || []).forEach((pos) => {
    if (pos.isGroup || pos.type === 'percent' || pos.taxRate == null) return;
    const netto = Math.abs(round(pos.price * pos.amount)) || 0;
    rateVolumes.set(pos.taxRate, (rateVolumes.get(pos.taxRate) || 0) + netto);
  });
  let dominantTaxRate = null;
  let dominantVolume = -1;
  rateVolumes.forEach((volume, rate) => {
    if (volume > dominantVolume) {
      dominantVolume = volume;
      dominantTaxRate = rate;
    }
  });
  const orderTaxRate =
    dominantTaxRate ??
    dataCollection.orderTaxRate ??
    dataCollection?.templateMetaData?.taxRate ??
    19;

  // Preserve the per-invoice "show positions" toggle when rebuilding the
  // previous-invoice list from the order — the order payload doesn't carry
  // the pivot flag, so take it from the current collection (edit state or
  // the invoice resource) before defaulting to hidden.
  const knownShowPositions = new Map(
    (dataCollection.previousInvoices || []).map((inv) => [
      inv.id,
      inv.showPositions,
    ]),
  );
  const orderInvoices = (order.invoices || []).map((inv) => ({
    ...inv,
    showPositions:
      knownShowPositions.get(inv.id) ?? inv.showPositions ?? false,
  }));
  // Invoices added to the list manually (no position linked to any order —
  // e.g. one written before the order existed) are not part of
  // order.invoices. Keep them, or an edit would drop them from the list and
  // the next save would detach them from the final invoice. Invoices whose
  // positions ARE order-linked are intentionally not carried over: they
  // belong to a previously selected order and must not survive a switch.
  const orderInvoiceIds = new Set(orderInvoices.map((inv) => inv.id));
  const manuallyAdded = (dataCollection.previousInvoices || []).filter(
    (inv) =>
      !orderInvoiceIds.has(inv.id) &&
      !(inv.positions || []).some((pos) => pos.orderId),
  );
  const previousInvoices = [...orderInvoices, ...manuallyAdded];

  const orderData = {
    previousInvoices,
    orderTitle: order.title,
    orderPrice: round(order.volume),
    orderNr: order.nr,
    orderPositions: order.positions,
    orderStartDate: order.startDate,
    orderTaxAmount: order.taxVolume,
    orderDiscountPercent:
      order.discountPercent || dataCollection.orderDiscountPercent || 0,
    orderTaxRate,
    isFlat: order.hasFlatInvoice || false,
    ...(order.project && { project: order.project }),
  };

  // Sum of the already-invoiced (order-effective) amount of previous invoices.
  const useIneffective = dataCollection?.templateMetaData?.useOrderIneffective;
  let previousInvoicesSum = 0;
  order.invoices?.forEach((inv) => {
    previousInvoicesSum += getInvoiceDeduction(inv, useIneffective).netto;
  });

  return { ...orderData, previousInvoicesSum };
}
